<template>
    <v-card class="scrollable-container pb-2 elevation-5">
        <v-card-title>
            <data-search-bar
                :title="title"
                :help-href="helpUrl"
                :options="dataSearchBarOptions"
                @change="onDataSearchBarChange"
                @refresh="onRefreshClick"
            >
                <template v-slot:below-title>
                    <div>
                        <span class="text-subtitle-2 pl-4">Type:</span>
                        <v-btn-toggle
                            v-model="typeFilter"
                            dense
                            mandatory
                            color="accent"
                            group
                            @change="onApplyFilters"
                        >
                            <v-btn value="SPACE">Space</v-btn>
                            <v-btn value="DEVICE">Device</v-btn>
                        </v-btn-toggle>
                    </div>
                </template>
            </data-search-bar>
        </v-card-title>
        <v-card-text class="scrollable-content pb-0">
            <data-table-view
                v-model="selectedItems"
                :headers="tableFields"
                :items="tagsWithCounts"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                :page="page"
                :items-per-page="itemsPerPage"
                :loading="isDataLoading"
                :zebra-stripping="zebraStripping"
                :computed-fields="computedFields"
                :show-select="true"
                :toolbar-actions="toolbarActions"
                toolbar-class="mx-4"
                allow-export
                @update-sort-by="onUpdateSortBy"
                @export="onExportDataClick"
                @toolbar="onToolbarClick"
            >
                <!-- ============== Table actions template ============== -->
                <template v-slot:tableButtons="{ item }">
                    <span
                        v-show="$can('show', 'Buttons/tags/deleteTag')"
                        class="px-3"
                    >
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-icon
                                    color="accent"
                                    cy-data="view-row"
                                    @click="onDeleteTagClick(item, $event)"
                                    v-on="on"
                                    >{{ icons.delete }}
                                </v-icon>
                            </template>
                            <span class="text-caption">Delete tag</span>
                        </v-tooltip>
                    </span>
                </template>
            </data-table-view>
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-show="
                            $can('create', 'Tag') &&
                                $can('show', 'Buttons/tags/addTag')
                        "
                        fab
                        fixed
                        right
                        bottom
                        small
                        color="primary"
                        class="mb-5 mr-1"
                        aria-label="Add new tag"
                        cy-data="add-row"
                        v-on="on"
                        @click="onAddTagClick"
                    >
                        <v-icon>{{ icons.add }}</v-icon>
                    </v-btn>
                </template>
                <span>Add new tag</span>
            </v-tooltip>
            <component
                :is="tagDetailsComponent"
                :show="showTagDetailsDialog"
                @close="onTagDetailsDialogClose"
            ></component>
            <component
                :is="exportDialogComponent"
                title="Export to CSV: Tags"
                :entity-name="exportEntityName"
                :data-source-params="exportParams"
                :output-fields="exportFields"
                :output-file-name="exportFileName"
                @close="onExportDialogClose"
            ></component>
        </v-card-text>
        <confirmation-dialog ref="confirmation"></confirmation-dialog>
        <lineup-selection ref="lineupSelection"></lineup-selection>
    </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { isEmpty } from '@/common/helpers';
import DataSearchBar from '@/components/Data/DataSearchBar';
import DataTableView from '@/components/Data/DataTableView';
import CsvDataExporter from '@/components/Export/CsvDataExporter';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import TagDetailsDialog from './TagDetailsDialog';
import LineupSelection from '../venues/components/LineupSelectionDialog';

export default {
    name: 'TagsPage',
    components: {
        'data-search-bar': DataSearchBar,
        'data-table-view': DataTableView,
        'tag-details-dialog': TagDetailsDialog,
        'csv-data-exporter': CsvDataExporter,
        'confirmation-dialog': ConfirmationDialog,
        'lineup-selection': LineupSelection
    },
    data() {
        return {
            title: 'Tags',
            isDataLoading: false,
            tagDetailsComponent: '',
            showTagDetailsDialog: false,
            exportDialogComponent: '',
            exportEntityName: 'tags',
            exportParams: {},
            typeFilter: '',
            lastFocusedElement: null,
            selectedItems: []
        };
    },
    computed: {
        ...mapState('tags', [
            'tableFields',
            'computedFields',
            'filterFields',
            'exportFields',
            'exportFileName',
            'requestQuery',
            'responseQuery',
            'filterQuery',
            'zebraStripping',
            'tagCounts',
            'helpUrl'
        ]),
        ...mapState('tags', {
            tags: 'items'
        }),
        dataSearchBarOptions() {
            return {
                page: this.requestQuery.number,
                itemsPerPage: this.requestQuery.size,
                pageCount: this.responseQuery.totalPages,
                itemsLength: this.responseQuery.totalElements,
                query: this.requestQuery.q
            };
        },
        sortBy() {
            return this.requestQuery.sort;
        },
        sortDesc() {
            return this.requestQuery.desc;
        },
        page() {
            return this.requestQuery.number;
        },
        itemsPerPage() {
            return this.requestQuery.size;
        },
        filters() {
            return {
                ...this.filterQuery,
                type: this.typeFilter
            };
        },
        tagsWithCounts() {
            if (!isEmpty(this.tagCounts)) {
                return this.tags.map(item => {
                    return {
                        ...item,
                        count: this.tagCounts[item.id] || 0
                    };
                });
            }
            return this.tags;
        },
        toolbarActions() {
            const actions = [];
            if (this.typeFilter === 'SPACE') {
                actions.push(...this.spaceToolbarActions);
            } else if (this.typeFilter === 'DEVICE') {
                actions.push(...this.deviceToolbarActions);
            }
            return actions;
        },
        deviceToolbarActions() {
            let actions = [];
            if (this.$can('control', 'Device')) {
                actions.push({
                    name: 'resetDevice',
                    text: 'Reset Devices',
                    icon: 'reset',
                    tooltip: 'Reset devices to factory settings'
                });
                actions.push({
                    name: 'rebootDevice',
                    text: 'Reboot Devices',
                    icon: 'reboot',
                    tooltip: 'Turn devices off, and then on'
                });
                actions.push({
                    name: 'setClosedCaptioning',
                    params: { flag: 'ON' },
                    text: 'Closed Caption On',
                    icon: 'closedCaption'
                });
                actions.push({
                    name: 'setClosedCaptioning',
                    params: { flag: 'OFF' },
                    text: 'Closed Caption Off',
                    icon: 'closedCaptionOff'
                });
                actions.push({
                    name: 'setTalkingGuide',
                    params: { flag: 'ON' },
                    text: 'Talking Guide On',
                    icon: 'talkingGuide'
                });
                actions.push({
                    name: 'setTalkingGuide',
                    params: { flag: 'OFF' },
                    text: 'Talking Guide Off',
                    icon: 'talkingGuideOff'
                });
            }
            return actions;
        },
        spaceToolbarActions() {
            let actions = [];
            if (this.$can('control', 'Space')) {
                actions.push({
                    name: 'reset',
                    text: 'Reset Rooms',
                    icon: 'reset',
                    tooltip: 'Reset rooms account'
                });
                actions.push({
                    name: 'checkin',
                    text: 'Check In',
                    icon: 'checkin'
                });
                actions.push({
                    name: 'checkout',
                    text: 'Check Out',
                    icon: 'checkout'
                });
                actions.push({
                    name: 'assignChannelLineup',
                    text: 'Assign Channel Lineup',
                    icon: 'tv'
                });
            }
            return actions;
        }
    },
    async created() {
        if (this.tableFields && !this.tableFields.length) {
            await this.setupView();
            await this.resetRequestQuery();
        }
    },
    async mounted() {
        this.isDataLoading = true;
        await this.fetchTagCounts();
        setTimeout(() => {
            this.typeFilter = this.filters.type || 'DEVICE';
            this.findTags(this.getInitialPageParams(), this.filterQuery);
        }, 200);
    },
    methods: {
        // =================== Vuex Actions =====================
        ...mapActions('tags', [
            'fetchTags',
            'setupView',
            'resetRequestQuery',
            'fetchTagCounts'
        ]),
        ...mapActions('spaces', {
            spaceBulkActions: 'postBulkActions',
            fetchSpaces: 'fetchItems'
        }),
        ...mapActions('devices', {
            deviceBulkActions: 'postBulkActions'
        }),
        ...mapActions('tag', ['deleteTag']),
        // ===================== Events ========================
        onDataSearchBarChange(options) {
            this.findTags(
                {
                    ...options,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filterQuery
            );
        },
        onUpdateSortBy(sortOptions) {
            if (sortOptions) {
                this.findTags(
                    {
                        ...this.dataSearchBarOptions,
                        ...sortOptions
                    },
                    this.filterQuery
                );
            }
        },
        onAddTagClick(event) {
            this.tagDetailsComponent = 'tag-details-dialog';
            this.showTagDetailsDialog = true;
            this.lastFocusedElement = event.target;
        },
        onTagDetailsDialogClose(refreshData) {
            this.showTagDetailsDialog = false;
            this.tagDetailsComponent = '';
            if (refreshData) {
                this.refreshView();
            }
            this.setNextFocus(this.lastFocusedElement);
        },
        onExportDataClick(event) {
            const searchParams = {
                number: 1,
                size:
                    this.dataSearchBarOptions.itemsLength <= 10000
                        ? this.dataSearchBarOptions.itemsLength
                        : 10000,
                q: this.dataSearchBarOptions.query,
                sort: this.sortBy,
                desc: this.sortDesc
            };
            const filterParams = this.filterQuery;
            this.exportParams = {
                searchParams,
                filterParams
            };
            this.exportDialogComponent = 'csv-data-exporter';
            this.lastFocusedElement = event.target;
        },
        onExportDialogClose() {
            this.exportDialogComponent = '';
            this.setNextFocus(this.lastFocusedElement);
        },
        onApplyFilters() {
            this.findTags(
                {
                    ...this.dataSearchBarOptions,
                    page: 1,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filters
            );
        },
        onRefreshClick() {
            this.refreshView();
        },
        async onDeleteTagClick(item) {
            if (item.count > 0) {
                this.showCannotDeleteMessage(item);
            } else {
                const res = await this.showDeleteConfirmation(item);
                if (res) {
                    const notifyConfig = {
                        notifyRequestPending: true,
                        notifySuccess: true,
                        notifyError: true,
                        successMessage: 'Tag has been deleted successfully.'
                    };
                    await this.deleteTag({ tagId: item.id, notifyConfig });
                    setTimeout(() => {
                        this.refreshView();
                    }, 1500);
                }
                this.selectedItems = [];
            }
        },
        async onToolbarClick({ item, selected }) {
            const action = item.name;
            const criteria = {
                tagIds: selected
                    .filter(item => item.count > 0)
                    .map(item => item.id)
            };
            let params = item.params || {};
            let needsRefresh = false;

            const emptyTags = selected.filter(item => item.count === 0);
            if (emptyTags.length === selected.length && action !== 'delete') {
                this.$nextTick(() => {
                    this.showEmptyTagDialog(this.typeFilter);
                });
                return;
            }
            if (action === 'reset' && this.typeFilter === 'SPACE') {
                // need to get spaces that have the selected tags
                let res = await this.fetchSpaces({
                    searchParams: { size: 9999 },
                    filterParams: { 'vmsTags.id': criteria.tagIds.join(',') }
                });
                const spaces = res.data.content;
                const deviceIds = this.getSpaceDevices(spaces).map(
                    item => item.id
                );
                if (deviceIds && deviceIds.length) {
                    await this.deviceBulkActions({
                        action: 'resetDevice',
                        criteria: { ids: deviceIds }
                    });
                    needsRefresh = true;
                } else {
                    this.showNoDevicesDialog();
                }
            } else if (
                this.typeFilter === 'SPACE' &&
                criteria.tagIds &&
                criteria.tagIds.length
            ) {
                if (action === 'assignChannelLineup') {
                    const res = await this.$refs.lineupSelection.open(
                        'Select a Channel Lineup'
                    );
                    if (res && res.id) {
                        params = { venueChannelMapId: res.id };
                    } else {
                        return;
                    }
                }
                await this.spaceBulkActions({ action, criteria, params });
                needsRefresh = true;
            } else if (
                this.typeFilter === 'DEVICE' &&
                criteria.tagIds &&
                criteria.tagIds.length
            ) {
                await this.deviceBulkActions({ action, criteria, params });
                needsRefresh = true;
            }
            if (needsRefresh) {
                // need to wait for DS search to refresh the data
                setTimeout(() => {
                    this.refreshView();
                }, 1500);
                this.selectedItems = [];
            }
        },
        // ===================== Others ========================
        refreshView() {
            this.findTags(
                {
                    ...this.dataSearchBarOptions,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filterQuery
            );
        },
        async findTags(searchOptions, filterOptions) {
            this.isDataLoading = true;
            try {
                if (!isEmpty(searchOptions)) {
                    const searchParams = {
                        number: searchOptions.page,
                        size: searchOptions.itemsPerPage,
                        q: searchOptions.query,
                        sort: searchOptions.sortBy,
                        desc: searchOptions.sortDesc
                    };
                    const filterParams = { ...filterOptions };
                    await this.fetchTags({ searchParams, filterParams });
                }
            } finally {
                this.isDataLoading = false;
            }
        },
        getInitialPageParams() {
            return {
                page: 1,
                itemsPerPage: this.itemsPerPage,
                query: this.requestQuery.q,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            };
        },
        setNextFocus(sourceElement) {
            const keydownEsc = new KeyboardEvent('keydown', { keyCode: 27 });
            this.$nextTick(() => {
                if (sourceElement) {
                    const button = sourceElement.closest('button');
                    button.focus();
                    button.dispatchEvent(keydownEsc);
                }
            });
        },
        async showEmptyTagDialog(type) {
            await this.$refs.confirmation.open(
                'Select Tags',
                `This action cannot be performed because the selected tags have a zero count ${type.toLowerCase()}s`,
                '',
                {
                    showIcon: true,
                    confirmActionText: '',
                    cancelActionText: 'OK'
                }
            );
            this.selectedItems = this.selectedItems.filter(
                item => item.count > 0
            );
        },
        async showNoDevicesDialog() {
            await this.$refs.confirmation.open(
                'Reset Space Devices',
                'Please select other tags',
                `The spaces for the selected tags, does not have any device to be reset.`,
                {
                    showIcon: true,
                    confirmActionText: '',
                    cancelActionText: 'OK'
                }
            );
            this.selectedItems = [];
        },
        showDeleteConfirmation(item) {
            return this.$refs.confirmation.open(
                'Delete Tags',
                `Are you sure you want to delete this tag: ${item.name}?`,
                '',
                {
                    showIcon: true,
                    confirmActionText: 'Confirm',
                    cancelActionText: 'Cancel'
                }
            );
        },
        async showCannotDeleteMessage(item) {
            await this.$refs.confirmation.open(
                'Delete Tags',
                '(count greater or equal one)',
                `This tag: ${item.name} cannot be deleted because it has been assigned alread! Try to remove this tag from the assigned device/space first`,
                {
                    showIcon: true,
                    confirmActionText: '',
                    cancelActionText: 'Cancel'
                }
            );
        },
        getSpaceDevices(spaces) {
            // will collect devices from a list of spaces
            let devices = [];
            spaces.forEach(space => {
                if (
                    space &&
                    space.serviceAccount &&
                    space.serviceAccount.devices
                ) {
                    devices.push(...space.serviceAccount.devices);
                }
            });
            return devices;
        }
    }
};
</script>

<style scoped lang="scss"></style>
